/* eslint-disable no-debugger */
import axios, { AxiosError, AxiosResponse } from "axios";
import { Dayjs } from "dayjs";

import { IPaginatedResponse } from "../@types/PaginatedResponse";
import {
  IAddPatientRequest,
  IAddPatientResponse,
  IArchivePatientRequest,
  IPatientDetailsDropdownItems,
  IPatientListVariants,
  IPatientSearchListItem,
  IPatientWards,
  IPortalPatientListResponse,
  ISetPatientWardsRequest,
  PatientAlertsTableContent,
} from "../@types/Patient";
import { IPatchOperation } from "../@types/Request";
import { patientListVariants } from "../constants/patientListVariants";
import { api } from "./api";

export const patientService = {
  async getPatients({
    variant,
    page,
    pageSize,
    sortBy,
    sortDir,
    customFieldIds,
  }: IPatientListVariants & {
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortDir?: "asc" | "desc";
    // EN-5067: comma-separated custom-field definition ids for the enabled custom columns.
    // Omitted entirely when no custom columns are on, so the list behaves exactly as before.
    customFieldIds?: string;
  }): Promise<AxiosResponse<IPortalPatientListResponse>> {
    const url = "/Portal/PatientList";
    const urlParamsParsed = new URLSearchParams();
    if (variant === patientListVariants.FLAGGED) {
      urlParamsParsed.append("selector", patientListVariants.FLAGGED);
    } else if (variant === patientListVariants.ALERTS) {
      urlParamsParsed.append("selector", patientListVariants.ALERTS);
    } else if (variant === patientListVariants.ARCHIVED) {
      urlParamsParsed.append("selector", patientListVariants.ARCHIVED);
    }
    urlParamsParsed.append("page", String(page ?? 1));
    urlParamsParsed.append("pageSize", String(pageSize ?? 10000));
    if (sortBy) urlParamsParsed.append("sortBy", sortBy);
    if (sortDir) urlParamsParsed.append("sortDir", sortDir);
    if (customFieldIds)
      urlParamsParsed.append("customFieldIds", customFieldIds);

    const patientsResponse = await api.get<IPortalPatientListResponse>(url, {
      params: urlParamsParsed,
    });

    return patientsResponse;
  },

  async getPatientNavigationIds({
    variant,
    sortBy,
    sortDir,
  }: IPatientListVariants & {
    sortBy?: string;
    sortDir?: "asc" | "desc";
  }): Promise<AxiosResponse<{ patientIds: number[] }>> {
    // Shares the Portal list endpoint so navigation traverses the same filtered
    // + sorted set the table shows, across all pages (not just the current one).
    const url = "/Portal/PatientList/NavigationIds";
    const urlParamsParsed = new URLSearchParams();

    if (variant === patientListVariants.FLAGGED) {
      urlParamsParsed.append("selector", patientListVariants.FLAGGED);
    } else if (variant === patientListVariants.ALERTS) {
      urlParamsParsed.append("selector", patientListVariants.ALERTS);
    } else if (variant === patientListVariants.ARCHIVED) {
      urlParamsParsed.append("selector", patientListVariants.ARCHIVED);
    }

    if (sortBy) urlParamsParsed.append("sortBy", sortBy);
    if (sortDir) urlParamsParsed.append("sortDir", sortDir);

    return api.get(url, { params: urlParamsParsed });
  },

  async getPatientSearchList(): Promise<
    AxiosResponse<IPatientSearchListItem[]>
  > {
    const url = "/Portal/Patients/SearchList";
    return api.get<IPatientSearchListItem[]>(url);
  },

  async getBillablePatients(fromDate: string, toDate: string) {
    const url = `/Portal/Patients/BillablePatients?fromDate=${fromDate}&toDate=${toDate}`;

    const patientsResponse = await api.get(url);

    return patientsResponse;
  },

  async getPatientById(id: string): Promise<AxiosResponse> {
    const url = `/Legacy/Patients/${id}`;
    const patient = await api.get(url);
    return patient;
  },

  async flagPatient({
    id: patientId,
    flagged,
  }: {
    id: string | number;
    flagged: boolean;
  }): Promise<AxiosResponse> {
    const url = `/Portal/Patients/${patientId}/Flag?flagged=${flagged}`;
    const urlParamsParsed = new URLSearchParams();

    return api.post(url, { params: urlParamsParsed });
  },

  async getPatientMonitoringInfo(id: string): Promise<AxiosResponse> {
    const url = `/Legacy/MonitoringPortal/Patients/${id}`;
    const patient = await api.get(url);
    return patient;
  },

  async getPatientAlerts(
    id: string | number,
    page?: number,
    limit?: number,
    select?: string,
    orderby?: string
  ): Promise<AxiosResponse> {
    const url = "/Legacy/MeasurementAlertTrigger";
    const urlParamsParsed = new URLSearchParams();

    if (id) {
      urlParamsParsed.append("id", id.toString());
    }
    if (page) {
      urlParamsParsed.append("page", page.toString());
    }
    if (limit) {
      urlParamsParsed.append("limit", limit.toString());
    }
    if (select) {
      urlParamsParsed.append("select", select);
    }
    if (orderby) {
      urlParamsParsed.append("orderby", orderby);
    }

    const patientsResponse = await api.get(url, {
      params: urlParamsParsed,
    });

    return patientsResponse;
  },

  async getPatientAlertsPaginated(
    id: string | number,
    page?: number,
    pageSize?: number
  ): Promise<AxiosResponse<IPaginatedResponse<PatientAlertsTableContent[]>>> {
    const url = "/Legacy/MeasurementAlertTrigger";

    const params: Record<string, string | number> = {
      id,
    };

    if (page !== undefined) {
      params.page = page;
    }
    if (pageSize !== undefined) {
      params.limit = pageSize;
    }

    return api.get<IPaginatedResponse<PatientAlertsTableContent[]>>(url, {
      params,
    });
  },

  async resolveTriggerAlert(
    idList: string[],
    patientId: number,
    outcome?: any,
    note?: string
  ): Promise<AxiosResponse> {
    const url = "/Legacy/TriggeredMeasurementAlert/ResolveAlerts";

    const patientsResponse = await api.post(url, {
      triggerId: idList,
      outcome,
      note,
      patientId,
    });

    return patientsResponse;
  },

  async getPatientPdf(
    patientId: number,
    fromDate: Dayjs,
    toDate: Dayjs
  ): Promise<AxiosResponse> {
    const url = "/Portal/Report/GeneratePatientMeasurementsReport";

    const result = await api.post(
      url,
      {
        patientId,
        fromDate,
        toDate,
      },
      { responseType: "blob" }
    );

    return result;
  },

  async updatePatientDetailsAndCardsOrder(
    patientId: number,
    operations: IPatchOperation[],
    cardsOrderJson: string
  ): Promise<AxiosResponse> {
    const url = "/Portal/Patients/UpdatePatientDetailsAndCardsOrder";

    const result = await api
      .post(url, {
        patientId,
        operations,
        cardsOrderJson,
      })
      .catch((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (result) {
      return result;
    }

    throw Error;
  },

  async getPatientDetailsDropDownItems(): Promise<
    AxiosResponse<IPatientDetailsDropdownItems>
  > {
    const url = "/Portal/Patients/GetPatientDetailsDropdownItems";

    const result = await api.get<IPatientDetailsDropdownItems>(url);

    return result;
  },

  async addPatient(
    patient: IAddPatientRequest
  ): Promise<AxiosResponse<IAddPatientResponse>> {
    const url = "/Portal/Patients/AddPatient";

    const serverResponse = await api
      .post<IAddPatientResponse>(url, patient)
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  // EN-4960: the patient's full ward set (and primary), for the side-panel ward chips.
  async getPatientWards(
    patientId: number
  ): Promise<AxiosResponse<IPatientWards>> {
    const url = `/Portal/Patients/${patientId}/Wards`;
    return api.get<IPatientWards>(url);
  },

  async setPatientWards(
    patientId: number,
    request: ISetPatientWardsRequest
  ): Promise<AxiosResponse> {
    const url = `/Portal/Patients/${patientId}/Wards`;
    return api.put(url, request);
  },

  async validatePhoneNumber(
    phoneNumber: string
  ): Promise<AxiosResponse<string>> {
    const url = "/Portal/Patients/ValidatePhoneNumber";

    const serverResponse = await api
      .post<string>(url, phoneNumber)
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async confirmValidatePhoneNumber(
    otpCode: string
  ): Promise<AxiosResponse<string>> {
    const url = "/Portal/Patients/ConfirmNumberValidation";

    const serverResponse = await api
      .post<string>(url, otpCode)
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async archivePatient(
    request: IArchivePatientRequest
  ): Promise<AxiosResponse<string>> {
    const url = "/Portal/Patients/ChangeArchiveStatus";

    const serverResponse = await api
      .post<string>(url, request)
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async resendOnboardingEmail(patientId: number): Promise<AxiosResponse> {
    return api.post(`/Portal/Patients/${patientId}/ResendOnboardingEmail`);
  },
};
